import { useNavigate } from "react-router-dom";
import { useEventCallback } from "usehooks-ts";

import type { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import type { InferenceFormNavigationState } from "@/components/inference-forms/constants";
import { useSnackbar } from "@/components/ui-v2";
import { getDefaultReference } from "@/components/ui-v2/References/utils";
import { NEW_SESSION_ID } from "@/features/forge/constants";

import { useConvertAssetSnapshotToFile } from "../../useConvertAssetSnapshotToFile";
import { useRequiredWorkspace } from "../../useRequiredWorkspace";
import type { ActivateAsset, ActivateFile } from "../types";
import { convertToRefImage } from "../utils";
import { GENERIC_ERROR } from "./constants";
import {
  applyValidator,
  navigateToForm,
  prepareSourceImages,
  prepareSourceMeshes,
  prepareSourceVideos,
} from "./utils";

interface UseActivateInferenceFormProps {
  sessionId?: string;
}

export const useActivateInferenceForm = (
  props: UseActivateInferenceFormProps,
) => {
  const { sessionId } = props;
  const navigate = useNavigate();
  const { workspace } = useRequiredWorkspace();
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { convertAssetSnapshotToFile } = useConvertAssetSnapshotToFile({
    onError: () => {
      showError("Error processing file. Please try again.");
    },
  });

  const activateInferenceForm = useEventCallback(
    async (
      mode: SessionInferenceMode,
      sources: (ActivateFile | ActivateAsset)[],
    ) => {
      const navSessionId = sessionId || NEW_SESSION_ID;
      const path = `/${workspace.name}/session/${navSessionId}`;

      if (!sources[0]) {
        // Should never happen, but just in case.
        console.error("No source provided.");
        return;
      }

      if (sources.some((s) => s.__typename === "Asset")) {
        showInfo("Processing image(s)...");
      }
      const files = sources.filter(
        (s): s is ActivateFile => s.__typename === "File",
      );
      const navDeps = { navigate, showSuccess };
      switch (mode) {
        case "CREATE": {
          const source = sources[0];
          const refImage = await convertToRefImage({
            source,
            convertAssetSnapshotToFile,
          });
          if (!refImage) {
            showError("Unable to process image");
            return;
          }
          navigate(path, {
            state: {
              mode,
              form: {
                references: [
                  getDefaultReference({ type: "INIT_IMAGE", image: refImage }),
                ],
              },
            } satisfies InferenceFormNavigationState,
          });
          showSuccess("Ready to create variations of the image.", {
            messageId: "variation-ready",
          });
          return;
        }
        case "PROMPT_EDIT_VIDEO":
        case "UPSCALE_VIDEO":
        case "REFRAME_VIDEO":
        case "REMOVE_BACKGROUND_VIDEO":
        case "ADD_SFX_VIDEO": {
          const sourceVideos = prepareSourceVideos(files);
          return navigateToForm(mode, path, { sourceVideos }, navDeps);
        }
        case "PROMPT_EDIT_IMAGE":
        case "UPSCALE":
        case "REFRAME_IMAGE":
        case "REMOVE_BACKGROUND":
        case "VECTORIZE_IMAGE": {
          const sourceImages = applyValidator(
            await prepareSourceImages(sources, convertAssetSnapshotToFile),
            mode,
          );
          return navigateToForm(mode, path, { sourceImages }, navDeps);
        }
        case "CREATE_3_D": {
          const guidanceFiles = await prepareSourceImages(
            sources,
            convertAssetSnapshotToFile,
          );
          return navigateToForm(mode, path, { guidanceFiles }, navDeps);
        }
        case "CREATE_VIDEO": {
          const frames = await prepareSourceImages(
            sources,
            convertAssetSnapshotToFile,
          );
          const firstFrame = frames[0];

          if (!firstFrame) {
            showError(GENERIC_ERROR);
            return;
          }

          return navigateToForm(mode, path, { firstFrame }, navDeps);
        }
        case "RETEXTURE_MESH":
        case "REMESH":
        case "RIG_MESH": {
          const sourceMeshes = applyValidator(prepareSourceMeshes(files), mode);
          return navigateToForm(mode, path, { sourceMeshes }, navDeps);
        }
        default:
          return;
      }
    },
  );

  return { activateInferenceForm };
};
